import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Child } from '../../types/parent';
import { Session, PriceCalculation, PaidEnrollmentResponse } from '../../types/parentEnrollment';
import { getChildren } from '../../services/parentService';
import { getEnrollableSessions, calculateEnrollmentPrice, enrollChildren } from '../../services/parentEnrollmentService';
import { capturePayPalPayment, getPayPalConfig } from '../../services/paymentService';
import ChildSelectionCard from '../../components/parent/ChildSelectionCard';
import PriceSummary from '../../components/parent/PriceSummary';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const steps = ['Select Children & Sessions', 'Payment', 'Confirmation'];

/**
 * ParentEnrollmentPage - 3-Step Wizard for Parent Multi-Child Enrollment
 *
 * Step 1: Selection
 *  - Display children as selectable cards
 *  - Display sessions as selectable cards
 *  - Validate at least one child and one session selected
 *
 * Step 2: Payment
 *  - Calculate and display price
 *  - Optional discount code
 *  - PayPal checkout
 *
 * Step 3: Confirmation
 *  - Success message
 *  - Summary of enrollments created
 *  - Link to dashboard
 */
const EnrollmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 state
  const [children, setChildren] = useState<Child[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedChildIds, setSelectedChildIds] = useState<Set<number>>(new Set());
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Step 2 state
  const [discountCode, setDiscountCode] = useState('');
  const [priceCalculation, setPriceCalculation] = useState<PriceCalculation | null>(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [paypalConfig, setPaypalConfig] = useState<{ clientId: string; mode: string } | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Terminal capture failure: a capture error where money may have moved must
  // NOT silently re-enable the PayPal button and invite an immediate retry.
  const [captureFailed, setCaptureFailed] = useState(false);
  // Benign terminal outcome: the cart was already enrolled, so no charge happened.
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);

  // Step 3 state
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);

  // Map of approved-orderId -> PaidEnrollmentResponse. Keying by orderId means a
  // second createOrder cannot clobber the response onApprove needs, so we never
  // capture the wrong paymentId for the order PayPal actually approved.
  const enrollmentResponsesRef = useRef<Map<string, PaidEnrollmentResponse>>(new Map());

  // In-flight / cached createOrder guard. Ensures at most ONE backend
  // enrollChildren call per cart attempt even if PayPal invokes createOrder
  // multiple times in rapid succession.
  const createOrderPromiseRef = useRef<Promise<string> | null>(null);

  // Load children and sessions on mount
  useEffect(() => {
    loadData();
  }, []);

  // Reset the in-flight create-order guard whenever the cart changes so a new
  // cart starts a fresh single create. A changed selection or discount code
  // represents a different cart and must create a new order. Derive stable
  // primitive keys from the Sets so this is robust even if a future handler
  // were to mutate a Set in place rather than replacing it.
  const selectedChildKey = Array.from(selectedChildIds).sort((a, b) => a - b).join(',');
  const selectedSessionKey = Array.from(selectedSessionIds).sort((a, b) => a - b).join(',');
  useEffect(() => {
    createOrderPromiseRef.current = null;
    // Drop any previously created orders so the Map can't grow across carts.
    enrollmentResponsesRef.current.clear();
  }, [selectedChildKey, selectedSessionKey, discountCode]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [childrenData, sessionsData, configData] = await Promise.all([
        getChildren(),
        getEnrollableSessions(),
        getPayPalConfig()
      ]);

      setChildren(childrenData);
      setSessions(sessionsData);
      setPaypalConfig(configData);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to load enrollment data');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Toggle child selection
  const handleChildToggle = (childId: number) => {
    setSelectedChildIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(childId)) {
        newSet.delete(childId);
      } else {
        newSet.add(childId);
      }
      return newSet;
    });
  };

  // Step 1: Toggle session selection
  const handleSessionToggle = (sessionId: number) => {
    setSelectedSessionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Step 1 → Step 2 or Step 3: Calculate price and proceed
  // If price is $0, enroll directly and skip to confirmation
  // If price > $0, proceed to payment step
  const handleProceedToPayment = async () => {
    try {
      setCalculatingPrice(true);
      setError(null);

      const childIds = Array.from(selectedChildIds);
      const sessionIds = Array.from(selectedSessionIds);

      const calculation = await calculateEnrollmentPrice({
        childIds,
        sessionIds,
        discountCode: discountCode || undefined
      });

      setPriceCalculation(calculation);

      // If free ($0), enroll directly and skip to confirmation
      if (calculation.finalPrice === 0) {
        const response = await enrollChildren({
          childIds,
          sessionIds,
          discountCode: discountCode || undefined
        });

        // Type guard: check if this is a free enrollment response
        if (response.isFree && 'totalEnrollments' in response) {
          setPriceCalculation({
            ...calculation,
            totalEnrollments: response.totalEnrollments
          });
        }

        setEnrollmentSuccess(true);
        setActiveStep(2); // Skip to confirmation
      } else {
        // Paid session - proceed to payment step
        setActiveStep(1);
      }
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to calculate price');
    } finally {
      setCalculatingPrice(false);
    }
  };

  // Step 2: Recalculate price when discount code changes
  const handleRecalculatePrice = async () => {
    try {
      setCalculatingPrice(true);
      setError(null);

      const childIds = Array.from(selectedChildIds);
      const sessionIds = Array.from(selectedSessionIds);

      const calculation = await calculateEnrollmentPrice({
        childIds,
        sessionIds,
        discountCode: discountCode || undefined
      });

      setPriceCalculation(calculation);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to recalculate price');
    } finally {
      setCalculatingPrice(false);
    }
  };

  // Step 2: Create PayPal order
  // Note: This is only called for paid sessions (finalPrice > 0)
  // Free sessions skip directly to confirmation in handleProceedToPayment
  const createOrder = async (): Promise<string> => {
    // In-flight guard: reuse a pending/resolved create for the current cart
    // instead of issuing a second enrollChildren call.
    if (createOrderPromiseRef.current) {
      return createOrderPromiseRef.current;
    }

    const promise = (async () => {
      try {
        const childIds = Array.from(selectedChildIds);
        const sessionIds = Array.from(selectedSessionIds);

        const response = await enrollChildren({
          childIds,
          sessionIds,
          discountCode: discountCode || undefined
        });

        // TypeScript discriminated union: check isFree discriminator
        if (response.isFree) {
          throw new Error('Unexpected free enrollment at payment step');
        }

        // Key the response by the orderId PayPal will approve so a stale entry
        // can never cause us to capture the wrong paymentId.
        enrollmentResponsesRef.current.set(response.orderId, response);
        console.log('Created PayPal order:', response.orderId, 'with payment ID:', response.paymentId);
        return response.orderId;
      } catch (err: unknown) {
        // Allow a retry of order creation after a failure.
        createOrderPromiseRef.current = null;
        console.error('Error creating order:', err);
        const error = err as { message?: string };
        setError(error.message || 'Failed to create payment');
        throw new Error('Failed to create order');
      }
    })();

    createOrderPromiseRef.current = promise;
    return promise;
  };

  // Step 2: Handle PayPal approval
  const onApprove = async (data: { orderID: string }) => {
    setProcessingPayment(true);

    try {
      // Look up the response for the order PayPal actually approved.
      const response = enrollmentResponsesRef.current.get(data.orderID);

      if (!response || !('paymentId' in response)) {
        throw new Error('Payment ID is missing');
      }

      console.log(`Capturing PayPal payment. OrderID: ${data.orderID}, PaymentId: ${response.paymentId}`);

      const result = await capturePayPalPayment(data.orderID, String(response.paymentId));

      // Backend signalled the cart was already enrolled: no charge happened.
      // Treat as a benign terminal outcome (not success, not error).
      if (result.blocked) {
        setAlreadyEnrolled(true);
        return;
      }

      setEnrollmentSuccess(true);
      setActiveStep(2);
    } catch (err: unknown) {
      console.error('Error processing payment:', err);
      // Terminal failure: money may have moved. Keep the button disabled and
      // do NOT re-enable in finally, so the user cannot immediately retry.
      setCaptureFailed(true);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Navigation helpers
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleReturnToDashboard = () => {
    navigate('/parent/dashboard');
  };

  // Render helpers
  const isStep1Valid = selectedChildIds.size > 0 && selectedSessionIds.size > 0;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Enroll Children in Sessions
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }} data-testid="enrollment-stepper">
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Selection */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Select Children
          </Typography>

          {children.length === 0 ? (
            <Alert severity="info" sx={{ mb: 4 }}>
              You don't have any children linked to your account yet. Please add children before enrolling.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {children.map((child) => (
                <Grid item xs={12} sm={6} md={4} key={child.id}>
                  <ChildSelectionCard
                    child={child}
                    selected={selectedChildIds.has(child.id)}
                    onToggle={handleChildToggle}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Select Sessions
          </Typography>

          {sessions.length === 0 ? (
            <Alert severity="info" sx={{ mb: 4 }}>
              No sessions are currently available for enrollment.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {sessions.map((session) => (
                <Grid item xs={12} sm={6} key={session.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedSessionIds.has(session.id) ? 2 : 1,
                      borderColor: selectedSessionIds.has(session.id) ? 'primary.main' : 'divider',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => handleSessionToggle(session.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Checkbox
                          checked={selectedSessionIds.has(session.id)}
                          onChange={() => handleSessionToggle(session.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mr: 1, mt: -1 }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">{session.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {session.description}
                          </Typography>
                          <Chip
                            label={`$${session.basePrice.toFixed(2)}`}
                            color="primary"
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={session.enrollmentType}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button onClick={() => navigate('/parent/dashboard')} data-testid="cancel-button">Cancel</Button>
            <Button
              variant="contained"
              onClick={handleProceedToPayment}
              disabled={!isStep1Valid || calculatingPrice}
              data-testid="continue-to-payment-button"
            >
              {calculatingPrice ? <CircularProgress size={24} /> : 'Continue to Payment'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 2 (benign terminal): cart already enrolled, no charge made */}
      {activeStep === 1 && alreadyEnrolled && (
        <Box sx={{ py: 4 }} data-testid="already-enrolled-state">
          <Alert severity="info" sx={{ mb: 3 }}>
            These enrollments already exist; no charge was made.
          </Alert>
          <Button
            variant="contained"
            onClick={handleReturnToDashboard}
            data-testid="blocked-dashboard-button"
          >
            Go to Dashboard
          </Button>
        </Box>
      )}

      {/* Step 2: Payment */}
      {activeStep === 1 && !alreadyEnrolled && priceCalculation && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Review and Pay
          </Typography>

          {captureFailed && (
            <Alert severity="error" sx={{ mb: 3 }} data-testid="capture-terminal-error">
              Payment could not be completed — do not retry. Please check your
              dashboard or contact support to confirm the status of your payment.
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <PriceSummary priceCalculation={priceCalculation} />

              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label="Discount Code (optional)"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  sx={{ mb: 2 }}
                  data-testid="discount-code-input"
                />
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRecalculatePrice}
                  disabled={calculatingPrice}
                  data-testid="apply-discount-button"
                >
                  {calculatingPrice ? <CircularProgress size={24} /> : 'Apply Discount Code'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {paypalConfig && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Payment
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Complete your payment using PayPal or credit/debit card.
                  </Typography>

                  <PayPalScriptProvider
                    options={{
                      clientId: paypalConfig.clientId,
                      currency: 'USD',
                      intent: 'capture',
                    }}
                  >
                    <Box sx={{ minHeight: 200, mb: 3 }}>
                      <PayPalButtons
                        style={{
                          layout: 'vertical',
                          shape: 'rect',
                          color: 'gold'
                        }}
                        disabled={processingPayment || captureFailed}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={(err: unknown) => {
                          console.error('PayPal Buttons error:', err);
                          setError('There was an error with the payment process. Please try again.');
                        }}
                        onCancel={() => {
                          console.log('Payment cancelled');
                          setError('Payment was cancelled. Please try again when you\'re ready.');
                        }}
                      />
                    </Box>
                  </PayPalScriptProvider>

                  {processingPayment && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Processing payment...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button onClick={handleBack} data-testid="back-button">Back</Button>
          </Box>
        </Box>
      )}

      {/* Step 3: Confirmation */}
      {activeStep === 2 && enrollmentSuccess && priceCalculation && (
        <Box sx={{ textAlign: 'center', py: 4 }} data-testid="enrollment-confirmation">
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} data-testid="success-icon" />
          <Typography variant="h4" gutterBottom data-testid="confirmation-title">
            Enrollment Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }} data-testid="confirmation-message">
            Your children have been successfully enrolled in the selected sessions.
          </Typography>

          <Card sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Enrollment Summary
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Children:</strong> {priceCalculation.childCount}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Sessions:</strong> {priceCalculation.sessionCount}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Total Enrollments:</strong> {priceCalculation.totalEnrollments}
              </Typography>
              <Typography variant="body1">
                <strong>Amount Paid:</strong> ${priceCalculation.finalPrice.toFixed(2)}
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button variant="contained" onClick={handleReturnToDashboard} data-testid="return-to-dashboard-button">
                Return to Dashboard
              </Button>
            </CardActions>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default EnrollmentPage;
